/**
 * Module de tarification dynamique AD AFRICA
 * 
 * Ce module calcule un multiplicateur de prix basé sur:
 * - L'offre: nombre de chauffeurs disponibles dans la zone
 * - La demande: nombre de courses en attente/en cours
 * 
 * STRATÉGIE: Favoriser les BAISSES de prix pour attirer les clients
 * - Réductions fréquentes quand peu de demande (jusqu'à -20%)
 * - Hausses rares et modérées uniquement en très forte demande (max +30%)
 */

/**
 * Calcule le ratio offre/demande
 * @param {number} availableDrivers - Nombre de chauffeurs disponibles
 * @param {number} pendingBookings - Nombre de réservations en attente
 * @returns {number} - Ratio (> 1 = plus d'offre, < 1 = plus de demande)
 */
export const calculateSupplyDemandRatio = (availableDrivers, pendingBookings) => {
  // Si 0 réservations en attente ET des chauffeurs disponibles = faible demande = PROMO
  if (pendingBookings === 0 && availableDrivers > 0) {
    // Retourner un ratio élevé pour déclencher les promos
    return 3.0 + availableDrivers * 0.5; // Plus il y a de chauffeurs libres, plus le ratio est haut
  }
  
  // Éviter la division par zéro
  const demand = Math.max(pendingBookings, 1);
  const supply = Math.max(availableDrivers, 0);
  
  // Ratio simple: chauffeurs disponibles / courses en attente
  return supply / demand;
};

/**
 * Calcule le multiplicateur de prix basé sur le ratio offre/demande
 * STRATÉGIE ASYMÉTRIQUE: Baisses généreuses, hausses très limitées
 * 
 * @param {number} ratio - Ratio offre/demande
 * @param {object} config - Configuration de tarification dynamique
 * @returns {object} - { multiplier, discount, surge, promoPercentage }
 */
export const calculatePriceMultiplier = (ratio, config = {}) => {
  const {
    minMultiplier = 0.80,       // Prix minimum (20% de réduction max) - GÉNÉREUX
    maxMultiplier = 1.30,       // Prix maximum (+30% max) - MODÉRÉ
    highDemandThreshold = 0.3,  // Seuil très bas: surge seulement si ratio < 0.3 (3+ courses par chauffeur)
    lowDemandThreshold = 1.0    // Promo dès que ratio >= 1.0 (autant ou plus de chauffeurs que de courses)
  } = config;

  let multiplier = 1.0;
  let discount = false;
  let surge = false;
  let promoPercentage = 0;

  if (ratio < highDemandThreshold) {
    // TRÈS forte demande seulement (plus de 3 courses en attente par chauffeur)
    // Hausse modérée et progressive
    const demandIntensity = (highDemandThreshold - ratio) / highDemandThreshold;
    multiplier = 1 + (demandIntensity * (maxMultiplier - 1) * 0.5); // Facteur 0.5 pour limiter la hausse
    multiplier = Math.min(multiplier, maxMultiplier);
    surge = multiplier > 1.05; // Afficher surge seulement si > 5%
  } else if (ratio >= lowDemandThreshold) {
    // Faible demande = PROMO (dès qu'il y a autant ou plus de chauffeurs que de courses)
    // Plus il y a de chauffeurs disponibles, plus la réduction est importante
    let discountLevel = 0;
    
    if (ratio >= 5.0) {
      // Beaucoup plus de chauffeurs que de courses: -20%
      discountLevel = 0.20;
    } else if (ratio >= 3.0) {
      // Nettement plus de chauffeurs: -15%
      discountLevel = 0.15;
    } else if (ratio >= 2.0) {
      // Plus de chauffeurs: -10%
      discountLevel = 0.10;
    } else if (ratio >= 1.5) {
      // Légèrement plus de chauffeurs: -5%
      discountLevel = 0.05;
    } else if (ratio >= 1.0) {
      // Équilibre: -3%
      discountLevel = 0.03;
    }
    
    if (discountLevel > 0) {
      multiplier = 1 - discountLevel;
      multiplier = Math.max(multiplier, minMultiplier);
      discount = true;
      promoPercentage = Math.round(discountLevel * 100);
    }
  }
  // Si ratio entre highDemandThreshold et lowDemandThreshold: prix normal (multiplier = 1.0)

  return {
    multiplier: parseFloat(multiplier.toFixed(2)),
    discount,
    surge,
    promoPercentage, // ex: 10 pour -10%, 15 pour -15%
    ratio: parseFloat(ratio.toFixed(2))
  };
};

/**
 * Applique le multiplicateur dynamique à un tarif
 * @param {number} baseFare - Tarif de base calculé
 * @param {number} multiplier - Multiplicateur dynamique
 * @param {number} decimal - Nombre de décimales
 * @returns {number} - Tarif ajusté
 */
export const applyDynamicMultiplier = (baseFare, multiplier, decimal = 0) => {
  const adjustedFare = baseFare * multiplier;
  return parseFloat(adjustedFare.toFixed(decimal));
};

/**
 * Calcule les statistiques de zone pour la tarification dynamique
 * @param {array} drivers - Liste des chauffeurs
 * @param {array} bookings - Liste des réservations actives
 * @param {object} location - Position actuelle { lat, lng }
 * @param {number} radiusKm - Rayon de recherche en km
 * @returns {object} - Statistiques de zone
 */
export const calculateZoneStats = (drivers = [], bookings = [], location = null, radiusKm = 10) => {
  // Si pas de localisation, utiliser toutes les données
  if (!location || !location.lat || !location.lng) {
    const availableDrivers = drivers.filter(d => 
      d.approved && d.driverActiveStatus && !d.queue
    ).length;
    
    const pendingBookings = bookings.filter(b => 
      b.status === 'NEW' || b.status === 'ACCEPTED'
    ).length;

    return {
      availableDrivers,
      pendingBookings,
      totalDrivers: drivers.length,
      totalBookings: bookings.length
    };
  }

  // Filtrer par distance si localisation fournie
  const getDistanceKm = (lat1, lng1, lat2, lng2) => {
    const R = 6371; // Rayon de la Terre en km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const driversInZone = drivers.filter(d => {
    if (!d.location || !d.approved || !d.driverActiveStatus || d.queue) return false;
    const dist = getDistanceKm(location.lat, location.lng, d.location.lat, d.location.lng);
    return dist <= radiusKm;
  });

  const bookingsInZone = bookings.filter(b => {
    if (!b.pickup || (b.status !== 'NEW' && b.status !== 'ACCEPTED')) return false;
    const dist = getDistanceKm(location.lat, location.lng, b.pickup.lat, b.pickup.lng);
    return dist <= radiusKm;
  });

  return {
    availableDrivers: driversInZone.length,
    pendingBookings: bookingsInZone.length,
    totalDrivers: drivers.length,
    totalBookings: bookings.length,
    radiusKm
  };
};

/**
 * Fonction principale: calcule la tarification dynamique complète
 * @param {object} params - Paramètres
 * @returns {object} - Résultat de tarification dynamique
 */
export const getDynamicPricing = (params = {}) => {
  try {
    const {
      baseFare = 0,
      drivers = [],
      bookings = [],
      location = null,
      settings = {},
      decimal = 0
    } = params;
    
    const dynamicConfig = (settings && settings.dynamicPricing) || {
      enabled: true,
      minMultiplier: 0.80,        // -20% max (généreux)
      maxMultiplier: 1.30,        // +30% max (modéré)
      highDemandThreshold: 0.3,   // Surge seulement si 3+ courses par chauffeur
      lowDemandThreshold: 1.0     // Promo dès équilibre offre/demande
    };

    // Si tarification dynamique désactivée
    if (!dynamicConfig.enabled) {
      return {
        originalFare: baseFare,
        adjustedFare: baseFare,
        multiplier: 1.0,
        discount: false,
        surge: false,
        promoPercentage: 0,
        dynamicEnabled: false
      };
    }

    const zoneStats = calculateZoneStats(
      drivers, 
      bookings, 
      location, 
      (settings && settings.driverRadius) || 10
    );

    const ratio = calculateSupplyDemandRatio(
      zoneStats.availableDrivers, 
      zoneStats.pendingBookings
    );

    const pricing = calculatePriceMultiplier(ratio, dynamicConfig);
    const adjustedFare = applyDynamicMultiplier(baseFare, pricing.multiplier, decimal);

    return {
      originalFare: baseFare,
      adjustedFare,
      multiplier: pricing.multiplier,
      discount: pricing.discount,
      surge: pricing.surge,
      promoPercentage: pricing.promoPercentage,
      dynamicEnabled: true,
      zoneStats,
      ratio: pricing.ratio
    };
  } catch (error) {
    console.error('[DynamicPricing] Error in getDynamicPricing:', error);
    return {
      originalFare: 0,
      adjustedFare: 0,
      multiplier: 1.0,
      discount: false,
      surge: false,
      promoPercentage: 0,
      dynamicEnabled: false,
      error: error.message
    };
  }
};

export default {
  calculateSupplyDemandRatio,
  calculatePriceMultiplier,
  applyDynamicMultiplier,
  calculateZoneStats,
  getDynamicPricing
};
